import { useMutation, useQueryClient } from '@tanstack/react-query'
import { documentKeys } from '@/entities/document'
import { type Slot, type SlotEntityKind, SlotSchema, slotKeys } from '@/entities/slot'
import { apiRequest } from '@/shared/api'

// CRUD слотов сущности. После мутации перечитываем слоты владельца и его
// документы (группировка листинга зависит от набора слотов).
function useSlotMutation<V>(
  entityKind: SlotEntityKind,
  entityId: string,
  fn: (vars: V) => Promise<Slot | null>,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: slotKeys.byEntity(entityKind, entityId) })
      void qc.invalidateQueries({ queryKey: documentKeys.byEntity(entityKind, entityId) })
    },
  })
}

export function useAddSlot(entityKind: SlotEntityKind, entityId: string) {
  return useSlotMutation(
    entityKind,
    entityId,
    // customer_can_upload шлёт только сотрудник; у заказчика сервер ставит true.
    async (vars: { name: string; customer_can_upload?: boolean }) =>
      SlotSchema.parse(
        await apiRequest({
          method: 'POST',
          url: '/slots',
          body: { entity_kind: entityKind, entity_id: entityId, ...vars },
        }),
      ),
  )
}

export function useRenameSlot(entityKind: SlotEntityKind, entityId: string) {
  return useSlotMutation(
    entityKind,
    entityId,
    async (vars: { id: string; name?: string; customer_can_upload?: boolean }) =>
      SlotSchema.parse(
        await apiRequest({
          method: 'PATCH',
          url: `/document-slots/${vars.id}`,
          body: {
            ...(vars.name != null ? { name: vars.name } : {}),
            ...(vars.customer_can_upload !== undefined
              ? { customer_can_upload: vars.customer_can_upload }
              : {}),
          },
        }),
      ),
  )
}

export function useDeleteSlot(entityKind: SlotEntityKind, entityId: string) {
  return useSlotMutation(entityKind, entityId, async (id: string) => {
    await apiRequest({ method: 'DELETE', url: `/document-slots/${id}` })
    return null
  })
}
